import is from '@sindresorhus/is';
import type { ReactElement } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useSnackbars } from '../../app/error-handling/SnackBars';
import { jobProfileView, sidebar } from '../../company-settings/constants';
import { useActivateManagedTemplate } from '../../company-settings/tabs/job-architecture-tab/job-profile/api/use-activate-managed-template';
import { useCustomTemplate } from '../../company-settings/tabs/job-architecture-tab/job-profile/api/use-custom-template';
import type { DefineTemplateOption } from '../components/DefineJobProfileTemplateDialog';
import { DefineJobProfileTemplateDialog } from '../components/DefineJobProfileTemplateDialog';
import { useNavigateToSettings } from './use-navigate-to-settings';

type RequireJobProfileTemplate = {
    readonly dialog: ReactElement;
    readonly requireJobProfileTemplate: (run: () => void | Promise<void>) => void;
};

export const useRequireJobProfileTemplate = (jobArchitectureId: string): RequireJobProfileTemplate => {
    const customTemplateQuery = useCustomTemplate(jobArchitectureId);
    const { activate, isPending } = useActivateManagedTemplate(jobArchitectureId);
    const { navigateToSettings } = useNavigateToSettings();
    const { showErrorSnackbar } = useSnackbars();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const pendingRunRef = useRef<(() => void | Promise<void>) | null>(null);

    const close = useCallback((): void => {
        setIsDialogOpen(false);
        pendingRunRef.current = null;
    }, []);

    const requireJobProfileTemplate = useCallback(
        (action: () => void | Promise<void>): void => {
            if (!is.nullOrUndefined(customTemplateQuery.data)) {
                action();
                return;
            }

            pendingRunRef.current = action;
            setIsDialogOpen(true);
        },
        [customTemplateQuery.data],
    );

    const handleContinue = useCallback(
        async (option: DefineTemplateOption): Promise<void> => {
            if (option === 'custom') {
                navigateToSettings({
                    jobProfileView: jobProfileView.createCustom,
                    sidebar: sidebar.jobArchitecture.jobProfile,
                });
                setIsDialogOpen(false);
                pendingRunRef.current = null;
                return;
            }

            let activated = false;
            try {
                activated = await activate();
            } catch (error) {
                showErrorSnackbar(error);
                return;
            }

            if (!activated) {
                return;
            }

            const pendingRun = pendingRunRef.current;
            pendingRunRef.current = null;
            setIsDialogOpen(false);

            if (pendingRun !== null) {
                await pendingRun();
            }
        },
        [activate, navigateToSettings, showErrorSnackbar],
    );

    const dialog = useMemo(
        () => (
            <DefineJobProfileTemplateDialog
                isActivating={isPending}
                open={isDialogOpen}
                onClose={close}
                onContinue={handleContinue}
                onNavigateToSettings={() => navigateToSettings({ sidebar: sidebar.jobArchitecture.jobProfile })}
            />
        ),
        [close, handleContinue, isDialogOpen, isPending, navigateToSettings],
    );

    return { dialog, requireJobProfileTemplate };
};
